import { createSystem, defaultConfig, defineConfig } from '@chakra-ui/react'

const config = defineConfig({
  preflight: true,
  theme: {
    tokens: {
      fonts: {
        heading: { value: '\'Figtree\', sans-serif' },
        body: { value: '\'Figtree\', sans-serif' }
      },
      colors: {
        // Brand secondary (slate-blue)
        sec: {
          50: { value: '#FFFFFF' },
          100: { value: '#E1E1ED' },
          200: { value: '#C7C7D0' },
          300: { value: '#9F9FB1' },
          400: { value: '#8B8BA7' },
          500: { value: '#79799F' },
          600: { value: '#5D5D91' },
          700: { value: '#4E4E8B' },
          800: { value: '#3E3E83' },
          900: { value: '#2F2F7D' },
          950: { value: '#282876' }
        },
        // Brand primary (purple)
        primary: {
          50: { value: '#F9F5FF' },
          100: { value: '#E9CCFF' },
          200: { value: '#C9AEFF' },
          300: { value: '#AB90F7' },
          400: { value: '#9B7EF0' },
          500: { value: '#7B64D8' },
          600: { value: '#6350C0' },
          700: { value: '#2D2B71' },
          800: { value: '#252368' },
          900: { value: '#23215D' },
          950: { value: '#141234' }
        },
        // Neutral grays — used for surfaces, borders, text
        neutral: {
          50: { value: '#F9FAFB' },
          100: { value: '#F3F4F6' },
          200: { value: '#E5E7EB' },
          300: { value: '#D1D5DB' },
          400: { value: '#9CA3AF' },
          500: { value: '#6B7280' },
          600: { value: '#4B5563' },
          700: { value: '#374151' },
          800: { value: '#1F2937' },
          900: { value: '#111827' },
          950: { value: '#0A0F18' }
        },
        // UI info blue (for sticky sync states)
        infoBlue: {
          50: { value: '#EDF6FF' },
          100: { value: '#DCEEFF' },
          200: { value: '#C2E0FF' },
          300: { value: '#9FCCFF' },
          400: { value: '#74B0FF' },
          500: { value: '#4B91F1' },
          600: { value: '#2E73D1' },
          700: { value: '#235CAF' },
          800: { value: '#214D8F' },
          900: { value: '#203E72' },
          950: { value: '#132744' }
        },
        // UI warning amber (for failed save state)
        warningAmber: {
          50: { value: '#FFF8E8' },
          100: { value: '#FEEFCB' },
          200: { value: '#FDE29F' },
          300: { value: '#FBD06B' },
          400: { value: '#F8B84A' },
          500: { value: '#E59A26' },
          600: { value: '#C67814' },
          700: { value: '#9F5C14' },
          800: { value: '#824B18' },
          900: { value: '#6C3F18' },
          950: { value: '#40240D' }
        },
        // UI error red (for failed action state)
        errorRed: {
          50: { value: '#FFF0F0' },
          100: { value: '#FFE0E0' },
          200: { value: '#FFC6C6' },
          300: { value: '#FFA0A0' },
          400: { value: '#FF6F6F' },
          500: { value: '#F04F4F' },
          600: { value: '#CC3636' },
          700: { value: '#A92D2D' },
          800: { value: '#8B2A2A' },
          900: { value: '#732828' },
          950: { value: '#431313' }
        }
      }
    },

    semanticTokens: {
      colors: {
        // ─── Brand / Primary ──────────────────────────────────────
        // Use colorPalette="primary" on Chakra components, or use these directly.
        primary: {
          solid: { value: { base: '{colors.primary.500}', _dark: '{colors.primary.400}' } },
          contrast: { value: { base: '{colors.white}', _dark: '{colors.white}' } },
          fg: { value: { base: '{colors.primary.700}', _dark: '{colors.primary.200}' } },
          muted: { value: { base: '{colors.primary.100}', _dark: '{colors.primary.950}' } },
          subtle: { value: { base: '{colors.primary.200}', _dark: '{colors.primary.900}' } },
          emphasized: { value: { base: '{colors.primary.300}', _dark: '{colors.primary.700}' } },
          focusRing: { value: { base: '{colors.primary.500}', _dark: '{colors.primary.400}' } }
        },

        // ─── Rating ───────────────────────────────────────────────
        // The filled part of a star rating. Amber rather than the brand colour
        // because a star that isn't gold doesn't read as filled — the shape
        // carries the convention, and the colour has to go with it. Borrowed
        // from the amber scale, not from `fg.warning`: nothing here is a warning.
        rating: {
          filled: { value: { base: '{colors.warningAmber.500}', _dark: '{colors.warningAmber.400}' } }
        },

        // ─── Backgrounds ──────────────────────────────────────────
        // bg.canvas   — outermost page background
        // bg.surface  — cards, panels, modals
        // bg.subtle   — sidebars, table rows, section dividers
        // bg.muted    — hover states, disabled fields
        bg: {
          canvas: { value: { base: '{colors.neutral.50}', _dark: '{colors.neutral.950}' } },
          surface: { value: { base: '{colors.white}', _dark: '{colors.neutral.900}' } },
          subtle: { value: { base: '{colors.neutral.100}', _dark: '{colors.neutral.800}' } },
          muted: { value: { base: '{colors.neutral.200}', _dark: '{colors.neutral.700}' } },
          // Accent backgrounds — tinted with brand colour
          accent: { value: { base: '{colors.primary.50}', _dark: '{colors.primary.950}' } },
          accentSubtle: { value: { base: '{colors.primary.100}', _dark: '{colors.primary.900}' } },
          info: { value: { base: '{colors.infoBlue.100}', _dark: '{colors.infoBlue.900}' } },
          infoSubtle: { value: { base: '{colors.infoBlue.50}', _dark: '{colors.infoBlue.950}' } },
          warning: { value: { base: '{colors.warningAmber.100}', _dark: '{colors.warningAmber.900}' } },
          warningSubtle: { value: { base: '{colors.warningAmber.50}', _dark: '{colors.warningAmber.950}' } },
          error: { value: { base: '{colors.errorRed.100}', _dark: '{colors.errorRed.900}' } },
          errorSubtle: { value: { base: '{colors.errorRed.50}', _dark: '{colors.errorRed.950}' } }
        },

        // ─── Foreground / Text ────────────────────────────────────
        // fg.default  — body copy
        // fg.muted    — secondary labels, captions
        // fg.subtle   — placeholder text, disabled text
        // fg.onAccent — text placed on a brand-coloured background
        fg: {
          default: { value: { base: '{colors.neutral.900}', _dark: '{colors.neutral.50}' } },
          muted: { value: { base: '{colors.neutral.600}', _dark: '{colors.neutral.400}' } },
          subtle: { value: { base: '{colors.neutral.400}', _dark: '{colors.neutral.600}' } },
          onAccent: { value: { base: '{colors.white}', _dark: '{colors.white}' } },
          warning: { value: { base: '{colors.warningAmber.900}', _dark: '{colors.warningAmber.100}' } },
          error: { value: { base: '{colors.errorRed.900}', _dark: '{colors.errorRed.100}' } }
        },

        // ─── Borders ─────────────────────────────────────────────
        // border.default    — standard rule between elements
        // border.muted      — very quiet separator
        // border.emphasized — stronger divider, active/selected states
        border: {
          default: { value: { base: '{colors.neutral.200}', _dark: '{colors.neutral.700}' } },
          muted: { value: { base: '{colors.neutral.100}', _dark: '{colors.neutral.800}' } },
          emphasized: { value: { base: '{colors.neutral.300}', _dark: '{colors.neutral.600}' } },
          primary: { value: { base: '{colors.primary.500}', _dark: '{colors.primary.400}' } },
          info: { value: { base: '{colors.infoBlue.300}', _dark: '{colors.infoBlue.700}' } },
          warning: { value: { base: '{colors.warningAmber.300}', _dark: '{colors.warningAmber.700}' } },
          error: { value: { base: '{colors.errorRed.300}', _dark: '{colors.errorRed.700}' } }
        }
      }
    }
  }
})

export const system = createSystem(defaultConfig, config)
